import React, { useMemo, useState, useCallback } from 'react';
import DataTable from '../common/DataTable';
import { UI_COLORS } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { USER_ROLES } from '../../utils/constants';
import deliveryOrderService from '../../services/deliveryOrderService';
import ChallanSuccessModal from '../delivery-orders/ChallanSuccessModal';


const InvoiceSalesTable = ({
  sales = [],
  loading = false,
  selectedSales = [],
  onSelectSale,
  onSelectAll,
  userRole
}) => {
  const { t } = useTranslation();
  const [printingInvoiceId, setPrintingInvoiceId] = useState(null);
  // Who can reprint an invoice from any row, regardless of order status —
  // mirrors the reprint affordance on the Delivery Challan Printed page.
  const canReprintInvoice = [
    USER_ROLES.ADMIN,
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.ACCOUNT_USER,
    USER_ROLES.SALES_STAFF,
    USER_ROLES.OPERATIONS_MANAGER,
  ].includes(userRole);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return t('common.symbols.emDash');
    try {
      return new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  }, [t]);

  // A sales-daybook row IS a challan (one row per delivery sub-order), so print THAT
  // challan through the standard challan modal — the same document the Delivery
  // Challan page prints. The bespoke template this replaced fetched the parent ORDER
  // and rendered order-level data (the order's invoice number, ordered amounts, its
  // own HSN maths), so the print never matched the row it was clicked on.
  const [challanData, setChallanData] = useState(null);
  const handlePrintChallan = useCallback(async (subOrderId) => {
    if (!subOrderId) return;
    setPrintingInvoiceId(subOrderId);
    try {
      const res = await deliveryOrderService.getDeliverySubOrderById(subOrderId);
      if (res && res.success && res.data) {
        setChallanData(res.data);
      } else {
        alert(t('common.messages.unableToLoadOrderForPrinting'));
      }
    } catch (e) {
      alert(t('common.messages.unableToLoadOrderForPrinting'));
    } finally {
      setPrintingInvoiceId(null);
    }
  }, [t]);

  const selectionColumn = useMemo(() => ({
    id: 'selection',
    sortable: false,
    width: '40px',
    renderHeader: () => {
      const currentPageIds = sales.map(s => s.id);
      const isAllSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedSales.includes(id));
      return (
        <input 
          type="checkbox" 
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          checked={isAllSelected}
          onChange={(e) => onSelectAll?.(e.target.checked, currentPageIds)} 
        />
      );
    },
    renderCell: (s) => {
      const id = s.id;
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <input 
            type="checkbox" 
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            checked={selectedSales.includes(id)}
            onChange={(e) => onSelectSale?.(id, e.target.checked)} 
          />
        </div>
      );
    },
    skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
  }), [sales, selectedSales, onSelectAll, onSelectSale]);

  const baseColumns = useMemo(() => [
    {
      id: 'date',
      label: t('invoiceSales.table.columns.date'),
      sortable: true,
      sortAccessor: (r) => (r.date ? new Date(r.date).getTime() : 0),
      renderCell: (r) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{formatDate(r.date)}</span>,
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'invoice_number',
      label: t('invoiceSales.table.columns.invoiceNumber'),
      sortable: true,
      sortAccessor: (r) => r.invoice_number || '',
      renderCell: (r) => <span className="text-sm">{r.invoice_number || t('common.symbols.emDash')}</span>,
      skeleton: <div className="w-36 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'order_number',
      label: t('invoiceSales.table.columns.orderNumber'),
      sortable: true,
      sortAccessor: (r) => r.order_number || '',
      renderCell: (r) => <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{r.order_number || t('common.symbols.emDash')}</div>,
      skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'tally_name',
      label: t('invoiceSales.table.columns.tallyName'),
      sortable: true,
      sortAccessor: (r) => (r.tally_name || '').toLowerCase(),
      renderCell: (r) => <span className="text-sm">{r.tally_name || t('common.symbols.emDash')}</span>,
      skeleton: <div className="w-36 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'vehicle_number',
      label: t('invoiceSales.table.columns.vehicleNumber'),
      sortable: true,
      sortAccessor: (r) => (r.vehicle_number || '').toLowerCase(),
      renderCell: (r) => <span className="text-sm">{r.vehicle_number || t('common.symbols.emDash')}</span>,
      skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'actual_quantity',
      label: "Total Quantity",
      sortable: true,
      sortAccessor: (r) => (r.actual_quantity ? parseFloat(r.actual_quantity) : 0),
      renderCell: (r) => <span className="text-sm">{r.actual_quantity != null ? `${r.actual_quantity}` : t('common.symbols.emDash')}</span>,
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
      align: 'right',
    },
    {
      id: 'total_amount',
      label: t('invoiceSales.table.columns.totalAmount'),
      sortable: true,
      sortAccessor: (r) => (r.total_amount ? parseFloat(r.total_amount) : 0),
      renderCell: (r) => <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{r.total_amount != null ? `${t('common.symbols.currency')}${Number(r.total_amount).toFixed(2)}` : t('common.symbols.emDash')}</span>,
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
      align: 'right',
    },
    {
      id: 'remark',
      label: t('invoiceSales.table.columns.remark'),
      sortable: true,
      sortAccessor: (r) => (r.remark || '').toLowerCase(),
      renderCell: (r) => {
        if (!r.remark) return <span style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.symbols.emDash')}</span>;
        return (
          <div 
            className="text-[11px] font-medium break-words leading-relaxed min-w-[200px] px-2 py-1.5 rounded border" 
            title={r.remark} 
            style={{ 
              backgroundColor: 'rgba(254, 243, 199, 0.5)', 
              color: '#92400e', 
              borderColor: 'rgba(217, 119, 6, 0.2)',
              whiteSpace: 'pre-wrap'
            }}
          >
            {r.remark}
          </div>
        );
      },
      skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
    },
  ], [t, formatDate]);

  const emptyState = useMemo(() => ({
    message: t('invoiceSales.table.emptyState.message'),
  }), [t]);

  // Add actions column separately so it can reference local handlers
  const actionsColumn = useMemo(() => ({
    id: 'actions',
    label: t('common.table.actions'),
    align: 'center',
    sortable: false,
    renderCell: (r) => {
      const id = r.sub_order_id; // daybook rows are challans; never fall back to another key
      return (
        <div className="flex items-center justify-center space-x-2">
          {canReprintInvoice && (
            <button
              onClick={() => handlePrintChallan(id)}
              disabled={printingInvoiceId === id}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: '#f3f4f6' }}
              title={t('orders.table.tooltips.printInvoice')}
            >
              {printingInvoiceId === id ? (
                <FontAwesomeIcon icon={faSpinner} spin className="text-sm" />
              ) : (
                <FontAwesomeIcon icon={faPrint} className="text-sm" />
              )}
            </button>
          )}
        </div>
      );
    },
    skeleton: (
      <div className="flex items-center justify-center space-x-2">
        <div className="w-8 h-8 bg-gray-300 rounded-lg" />
      </div>
    ),
  }), [handlePrintChallan, printingInvoiceId, canReprintInvoice, t]);

  const columns = useMemo(() => [selectionColumn, ...baseColumns, actionsColumn], [selectionColumn, baseColumns, actionsColumn]);

  return (
    <div className="bg-white mt-2 rounded-lg shadow-sm border border-gray-200">
      <DataTable
        data={Array.isArray(sales) ? sales : []}
        columns={columns}
        loading={loading}
        keyField="id"
        enablePagination={false}
        defaultSort={{ field: 'order_number', direction: 'asc' }}
        emptyState={emptyState}
      />
      {challanData && (
        <ChallanSuccessModal
          isOpen={!!challanData}
          data={challanData}
          onClose={() => setChallanData(null)}
          onMarkChallanPrinted={() => deliveryOrderService.markChallanPrinted(challanData.id)}
          userRole={userRole}
        />
      )}
    </div>
  );
};

export default InvoiceSalesTable;
